import React from 'react';
import { CelebrationIcon, TipIcon } from '../../../icons';

interface SparringCompletionCardProps {
  milestoneCount: number;
  onContinue: () => void;
}

/**
 * SparringCompletionCard - Success kaart na het genereren van mijlpalen
 * 
 * Toont een duidelijke bevestiging dat alles is opgeslagen en geeft
 * de gebruiker een duidelijke call-to-action om verder te gaan.
 * 
 * Zweeft als overlay bovenop de chat.
 */
const SparringCompletionCard: React.FC<SparringCompletionCardProps> = ({
  milestoneCount,
  onContinue,
}) => {
  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      style={{ animation: 'fadeIn 0.3s ease-out' }}
    >
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        @keyframes scaleIn {
          from {
            transform: scale(0.9);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
      `}</style>
      <div 
        className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl p-6 shadow-2xl max-w-md w-full"
        style={{ animation: 'scaleIn 0.3s ease-out' }}
      >
        {/* Success icon */}
        <div className="flex justify-center mb-4">
          <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center shadow-md">
            <span className="text-5xl">✓</span>
          </div>
        </div>

        {/* Success message */}
        <div className="text-center mb-6">
          <h3 className="text-2xl font-bold text-gray-900 mb-2">
            Gefeliciteerd! <CelebrationIcon size={24} className="inline" />
          </h3>
          <p className="text-lg text-gray-700 mb-3">
            Je sparring sessie is succesvol afgerond
          </p>
          
          {/* Stats */}
          <div className="flex justify-center gap-4 mb-4">
            <div className="bg-white rounded-lg px-4 py-3 shadow-sm border border-green-200">
              <div className="text-3xl font-bold text-green-600">{milestoneCount}</div>
              <div className="text-xs text-gray-600 font-medium">
                Mijlpa{milestoneCount === 1 ? 'al' : 'len'}
              </div>
            </div>
            <div className="bg-white rounded-lg px-4 py-3 shadow-sm border border-green-200">
              <div className="text-3xl font-bold text-blue-600">{milestoneCount}</div>
              <div className="text-xs text-gray-600 font-medium">
                Acti{milestoneCount === 1 ? 'e' : 'es'}
              </div>
            </div>
          </div>

          <p className="text-sm text-gray-600">
            Al je mijlpalen en acties zijn opgeslagen en klaar om te gebruiken.
          </p>
        </div>

        {/* Call to action */}
        <button
          onClick={onContinue}
          className="w-full py-4 px-6 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl text-lg font-bold hover:from-orange-600 hover:to-orange-700 transition-all shadow-md hover:shadow-lg"
        >
          <span className="flex items-center justify-center gap-2">
            <span>Bekijk mijn mijlpalen</span>
            <span>→</span>
          </span>
        </button>

        {/* Additional info */}
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-xs text-blue-800 text-center">
            <TipIcon size={14} className="inline" /> <strong>Tip:</strong> Je kunt je mijlpalen en acties altijd later nog aanpassen via de Journey pagina
          </p>
        </div>
      </div>
    </div>
  );
};

export default SparringCompletionCard;
